import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  TextField,
  Combobox,
  ComboboxInput,
  ComboboxList,
  Button,
  Banner,
  MaterialIcon
} from '@dsny/dsny-component-library'
import Tooltip from 'components/Tooltip'
import {
  ModalTitle,
  ModalDescription,
  TextFieldWrapper
} from '../../ActionsSection/ActionsSection.styles'
import {
  DescriptionCounter,
  ButtonWrapper,
  XButtonWrapper
} from 'components/Modals/CreatePlaylistModal/CreatePlaylistModal.styles'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { MUSIC_INTERACTION_ACTIONS } from 'utils/constants'
import {
  ComboboxPopoverStyled,
  ComboboxOptionStyled
} from 'components/ComboboxPopover/ComboboxPopover.styles'
import { ReleasesService } from 'services'

const MAX_DESCRIPTION_LIMIT = 3000

const schema = yup.object().shape({
  email: yup.string().email().required('Email is required'),
  requestType: yup.string().required('Request type is required'),
  message: yup
    .string()
    .max(MAX_DESCRIPTION_LIMIT)
    .required('Message is required')
})

export default function ContactModal({
  isContactModalOpen,
  setIsContactModalOpen
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const { profile } = useSelector((state) => state.profile)
  const { currentReleaseInformation } = useSelector((state) => state.releases)
  const [messageCount, setMessageCount] = useState(0)
  const [apiError, setApiError] = useState(null)
  const [isComboBoxOpen, setComboBoxOpen] = useState(false)
  const [requestTypeID, setRequestTypeID] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    resolver: yupResolver(schema)
  })
  const messageValue = watch('message')
  const releaseId = currentReleaseInformation?.releaseid

  const openCombobox = () => {
    setComboBoxOpen(true)
  }

  const closeCombobox = () => {
    setComboBoxOpen(false)
  }

  const cancelContactRequest = () => {
    setIsContactModalOpen(false)
  }

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const response = await ReleasesService.comment(releaseId, {
        player_email: profile?.mailto,
        comment_type_id: requestTypeID,
        comment_type: data?.requestType,
        comment_text: data?.message
      })

      if (response?.status === 200) {
        setIsSubmitting(false)
        setIsContactModalOpen(false)
        dispatch(
          NotificationActions.broadcastMessage(
            '',
            NotificationType.contactRequestSent
          )
        )
      }
    } catch (error) {
      setIsSubmitting(false)
      setApiError(true)
      console.log(error)
    }
  }

  useEffect(() => {
    setValue('email', profile?.mailto)
  }, [profile])

  useEffect(() => {
    if (messageValue) {
      setMessageCount(messageValue.length)
    }
  }, [messageValue])

  useEffect(() => {
    reset({
      email: profile?.mailto,
      requestType: '',
      message: ''
    })
    setMessageCount(0)
  }, [isContactModalOpen])

  return (
    <Modal
      isOpen={isContactModalOpen}
      onClose={() => setIsContactModalOpen(false)}
      containerStyles={{
        padding: '24px 64px 64px',
        background: theme.colors.neutralW20,
        border: `1px solid ${theme.colors.neutralW30}`,
        width: 676,
        height: 'auto'
      }}
    >
      <XButtonWrapper onClick={() => setIsContactModalOpen(false)}>
        <MaterialIcon name="X" color={theme.colors.neutralW80} />
      </XButtonWrapper>
      {apiError && (
        <Banner
          variant="error"
          mode="dark"
          style={{ marginBottom: 32, maxWidth: '100%' }}
          bannerContent={{
            title: t('release-info.contact-request-failed-title'),
            description: t('release-info.contact-request-failed-body')
          }}
        />
      )}

      <ModalTitle style={{ marginTop: 40 }}>
        {t('release-info.contact-modal-title')}
      </ModalTitle>
      <ModalDescription>
        {t('release-info.contact-modal-description')}
      </ModalDescription>

      <form onSubmit={handleSubmit((values) => onSubmit(values))}>
        <Tooltip
          hasArrow={false}
          placement="top"
          text={t('release-info.contact-modal-email-use')}
          containerStyles={{ width: 300, whiteSpace: 'normal', padding: 0 }}
          theme={theme}
          wrapperStyles={{ width: '100%' }}
        >
          <TextFieldWrapper style={{ marginBottom: 24 }}>
            <TextField
              id="email"
              name="email"
              {...register('email')}
              ref={null}
              inputRef={register('email').ref}
              wrapperStyles={{ paddingRight: 0 }}
              labelText={t('release-info.contact-modal-email-input-label')}
              setting="player"
              readOnly
              tooltip={t('release-info.contact-modal-email-use')}
              value={profile?.mailto}
              highlightColor={theme.colors.neutralW40}
              inputStyle={{ color: theme.colors.neutralW40 }}
            />
          </TextFieldWrapper>
        </Tooltip>

        <Controller
          name="requestType"
          control={control}
          render={({ field }) => (
            <Combobox
              label={t('release-info.contact-modal-type-input-label')}
              isOpen={isComboBoxOpen}
              onClick={openCombobox}
              onClose={closeCombobox}
              onChange={(newValue) => {
                const { description, subDescription } = newValue

                setRequestTypeID(description)
                setValue('requestType', subDescription)
              }}
              value={{ description: field?.value ? field?.value : '' }}
              style={{ width: '100%', marginBottom: 24 }}
            >
              <ComboboxInput
                inputLabelStyles={{
                  background: theme.colors.neutralW20,
                  color: theme.colors.neutralW100
                }}
                placeholder={t('release-info.contact-modal-select-type')}
                error={errors?.requestType?.message}
                style={{
                  color: theme.colors.neutralW100,
                  borderColor: errors?.requestType
                    ? theme.colors.error70
                    : theme.colors.neutralW50
                }}
              />
              <ComboboxPopoverStyled>
                <ComboboxList>
                  {Object.entries(MUSIC_INTERACTION_ACTIONS).map(
                    ([key, value]) => {
                      return (
                        <ComboboxOptionStyled
                          key={key}
                          value={{
                            description: key,
                            subDescription: t(`release-info.${value}`)
                          }}
                        >
                          {t(`release-info.${value}`)}
                        </ComboboxOptionStyled>
                      )
                    }
                  )}
                </ComboboxList>
              </ComboboxPopoverStyled>
            </Combobox>
          )}
        />

        <TextFieldWrapper>
          <TextField
            id="message"
            name="message"
            {...register('message')}
            ref={null}
            inputRef={register('message').ref}
            required
            multiline
            maxLength={MAX_DESCRIPTION_LIMIT}
            wrapperStyles={{ paddingRight: 0 }}
            inputStyles={{ fontSize: 14 }}
            placeholder={t('release-info.contact-modal-textarea-placeholder')}
            labelText={t('release-info.contact-modal-textarea-label')}
            setting="player"
            error={errors?.message?.message}
            inputStyle={{
              borderColor: errors?.message
                ? theme.colors.error70
                : theme.colors.neutralW50
            }}
            errorStyle={{ color: theme.colors.error70 }}
          />
          <DescriptionCounter>{`${messageCount}/${MAX_DESCRIPTION_LIMIT}`}</DescriptionCounter>
        </TextFieldWrapper>

        <ModalDescription>
          {t('release-info.contact-modal-email-disclaimer')}
        </ModalDescription>

        <ButtonWrapper>
          <Button
            variant="secondary"
            setting="player"
            mode="dark"
            style={{ marginRight: 16 }}
            onClick={cancelContactRequest}
          >
            {t('general.cancel')}
          </Button>

          <Button
            type="submit"
            variant="primary"
            setting="player"
            mode="dark"
            disabled={isSubmitting}
          >
            {t('release-info.contact-modal-submit-button')}
          </Button>
        </ButtonWrapper>
      </form>
    </Modal>
  )
}
